import axios from 'axios'
import { httpsCallable } from 'firebase/functions'
import { AppContext } from 'global/cnr/contexts/AppProvider'
import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { ammendMedia_alignment } from './mediaHelpers/sportAlignments'
import { ammendMedia_results } from './mediaHelpers/sportsResults'
import { SportsSeasonContext } from '../cnr/contexts/SportsSeasonContext'
import { sportsHelpers } from '../cnr/reducers/reducerHelpers/sportsHelpers'

export const useFetchMedia = ({
  concurrency = 4,
  delay = 750
} = {}) => {

  const appContext = useContext(AppContext);
  const { app_state } = appContext
  const { mainFunctions, app_alts, app_prods, mainStorage } = app_state

  const sportsSeasonContext = useContext(SportsSeasonContext)
  const { sportsSeason_state } = sportsSeasonContext ?? {}
  const {
    currentSeason_info,
    links_score,
    teams_info,
  } = sportsSeason_state ?? {}

  const { latestSeason, _sportsAbr, _sportsModeKey, _sportsModeSport } = currentSeason_info ?? {}

  const currentSeason = useMemo(() => {
    let _season = 'fall'
    switch (_sportsModeSport) {
      case 'soccer':
      case 'football':
        _season = 'fall'
        break;
      case 'basketball':
        _season = 'winter'
        break;
      case 'baseball':
      case 'softball':
        _season = 'spring'
        break;
      default:
    }
    return _season
  }, [_sportsModeSport])

  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // avoids race conditions if called twice
  const runIdRef = useRef(0)

  const getMedia_resultsOnly = useCallback(async (callData, withFunctions) => {

    const runId = ++runIdRef.current

    setLoading(true)
    setError(null)

    try {
      const {
        useSingleDate,
        singleDate,
        year,
      } = callData

      const _year = year % 100

      let { mediaUrl, mediaUrlDateReplace, mediaUrlYearReplace } = links_score ?? {}
      let _url = mediaUrl && mediaUrlYearReplace ? mediaUrl.replace(mediaUrlYearReplace, _year) : mediaUrl.replace('25', _year)
      const _mediaUrlDateReplace = mediaUrlDateReplace || 'TODAYSDATE'

      const _dateGroupKeys = sportsHelpers.generateDatesByYear([year], currentSeason)
      const _dateGroupKeys_current = useSingleDate && singleDate ? [singleDate] : _dateGroupKeys[year]

      if (!_dateGroupKeys_current?.length) {
        setData([])
        return []
      }

      if (withFunctions) {
        const getMediaScores = httpsCallable(mainFunctions, 'getMediaScores');
        const response = await getMediaScores(callData)
        console.log('response', response)
      }

      const mergedMatches = await runWithConcurrency(
        _dateGroupKeys_current,
        async date => {
          const _date = formatDate(date)
          const _url_date = _mediaUrlDateReplace ? _url.replace(_mediaUrlDateReplace, _date) : _url
          const html = await fetchWithRetry(_url_date,)
          return ammendMedia_results(callData, _date, html)
        },
        { concurrency, delay }
      )

      // ignore stale runs
      if (runId === runIdRef.current) {
        setData(mergedMatches)
      }

      return mergedMatches
      // console.log('mergedMatches', mergedMatches)
      // callback({ results: mergedMatches })

    } catch (err) {
      if (runId === runIdRef.current) {
        setError(err)
      }
      throw err
    } finally {
      if (runId === runIdRef.current) {
        setLoading(false)
      }
    }
  }, [concurrency, delay])

  const getMedia_standingsOnly = useCallback(async (callData) => {
    const runId = ++runIdRef.current
    setLoading(true)
    setError(null)

    try {
      const { links_score, year, existingTeams, database_fns } = callData
      let { mediaUrl } = links_score ?? {}
      const _year = year % 100
      const { mediaUrlDateReplace, mediaUrlYearReplace } = links_score ?? {}
      let _url = mediaUrl && mediaUrlDateReplace ? mediaUrl.replace('schedule/' + mediaUrlDateReplace, 'standings') : null
      _url = mediaUrl && mediaUrlYearReplace ? _url.replace(mediaUrlYearReplace, _year) : _url.replace('25', _year)

      console.log('_url', _url)
      const html = await fetchWithRetry(_url)
      // const doc = parser.parseFromString(result, 'text/html')
      const results = ammendMedia_alignment(html, existingTeams, _url, database_fns)
      // ignore stale runs
      if (runId === runIdRef.current) {
        setData(results)
      }

      console.log('results', results)
      return results
      // console.log('mergedMatches', mergedMatches)
      // callback({ results: mergedMatches })

    } catch (err) {
      if (runId === runIdRef.current) {
        setError(err)
      }
      throw err
    } finally {
      if (runId === runIdRef.current) {
        setLoading(false)
      }
    }
  }, [concurrency, delay])

  return {
    getMedia_resultsOnly,
    getMedia_standingsOnly,
    data,
    loading,
    error
  }
}

const sleep = ms => new Promise(r => setTimeout(r, ms))

const axiosInstance = axios.create({
  method: 'get',
  timeout: 30000,
  headers: {
    'Accept': 'text/html,application/xhtml+xml'
    // 'User-Agent': 'Mozilla/5.0',
  }
})

const fetchWithRetry = async (url, retries = 2) => {
  try {
    const res = await axiosInstance.get(url)
    return res.data
  } catch (err) {
    if (retries > 0 && err.code === 'ECONNABORTED') {
      await sleep(1000 * (3 - retries))
      return fetchWithRetry(url, retries - 1)
    }
    throw err
  }
}

const runWithConcurrency = async (
  items,
  handler,
  { concurrency = 4, delay = 750 } = {}
) => {
  const results = []
  const queue = [...items]

  const workers = Array.from({ length: concurrency }, async () => {
    while (queue.length) {
      const item = queue.shift()
      try {
        const res = await handler(item)
        results.push(res)
      } catch (err) {
        console.log('worker error', err.message)
      }
      await sleep(delay)
    }
  })

  await Promise.all(workers)
  return results.flat()
}

function formatDate(inputDate) {
  // Split the input date into components
  var dateComponents = inputDate.split('/');

  // Extract month, day, and year
  var month = dateComponents[0];
  var day = dateComponents[1];
  var year = dateComponents[2];

  // Create a new date in 'yyyy-mm-dd' format
  var formattedDate = year + '-' + month + '-' + day;

  return formattedDate;
}